import { useEffect, useMemo, useState } from "react";
import { getBusinessProfile } from "../../services/dashboardApi";
import { useLang } from "../../core/lang";
import LangKeys from "../../core/locales/keys";

import UploadLogoPage from "./business-profile/UploadLogoPage";
import EditFieldPage from "./business-profile/EditFieldPage";
import GstinPage from "./business-profile/GstinPage";
import BankDetailsPage from "./business-profile/BankDetailsPage";
import StaffCountPage from "./business-profile/StaffCountPage";

// ======================= Constants & Helpers =======================

const emptyProfile = {
  business_name: "",
  owner_name: "",
  mobile: "",
  email: "",
  address: "",
  city: "",
  state: "",
  area: "",
  pincode: "",
  latitude: null,
  longitude: null,
  is_store_online: 0,
  store_description: "",
  logo_url: "",
  gstin: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  staff_count: 0,
};

const calculateProfileStrength = (bp, t) => {
  if (!bp) {
    return { percent: 0, label: t(LangKeys.weak), color: "#DC2626" };
  }

  let score = 0;
  const isFilled = (value) => value && String(value).trim() !== "";

  if (isFilled(bp.business_name)) score += 15;
  if (isFilled(bp.owner_name)) score += 10;
  if (isFilled(bp.mobile)) score += 10;
  if (isFilled(bp.email)) score += 5;
  if (isFilled(bp.address)) score += 10;
  if (isFilled(bp.logo_url)) score += 10;
  if (isFilled(bp.gstin)) score += 15;
  if (isFilled(bp.bank_name) && isFilled(bp.account_number) && isFilled(bp.ifsc_code)) score += 15;
  if ((bp.staff_count || 0) > 0) score += 10;

  if (score <= 30) return { percent: score, label: t(LangKeys.weak), color: "#DC2626" };
  if (score <= 60) return { percent: score, label: t(LangKeys.average), color: "#F59E0B" };
  if (score <= 85) return { percent: score, label: t(LangKeys.good), color: "#2563EB" };
  return { percent: score, label: t(LangKeys.strong), color: "#16A34A" };
};

// ======================= Main Component =======================

function BusinessProfileHome() {
  const { t } = useLang();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [editField, setEditField] = useState(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const showToast = (msg, type = "success") => {
    setToastMessage({ msg, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await getBusinessProfile();

      if (res?.success && res?.data) {
        setProfile(res.data);
        localStorage.setItem("business_profile", JSON.stringify(res.data));
      } else {
        setProfile(emptyProfile);
      }
    } catch (error) {
      console.error("Failed to load business profile:", error);
      setProfile(emptyProfile);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activePage) {
      const cached = localStorage.getItem("business_profile");
      if (cached) {
        setProfile(JSON.parse(cached));
      }
    }
  }, [activePage]);

  const strength = useMemo(() => calculateProfileStrength(profile, t), [profile, t]);

  const toggleOnlineStore = async (e) => {
    const isChecked = e.target.checked;
    const newValue = isChecked ? 1 : 0;
    const stringValue = isChecked ? "1" : "0";

    const updated = {
      ...(profile || emptyProfile),
      is_store_online: newValue,
    };
    setProfile(updated);
    localStorage.setItem("business_profile", JSON.stringify(updated));

    try {
      const token = localStorage.getItem("token");
      await fetch("https://khata.moxpal.com/api/v2/index.php/business-profile", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          field: "is_store_online",
          value: stringValue,
        }),
      });
      showToast(
        isChecked
          ? t(LangKeys.biz_profile__online_business_active) || "Business is now Live Online! 🎉"
          : t(LangKeys.biz_profile__online_business_offline) || "Business is now Offline."
      );
    } catch (err) {
      console.error("Failed to toggle online store:", err);
      showToast("Failed to update status", "error");
      loadProfile();
    }
  };

  const handleGpsAutoFill = async () => {
    if (!navigator.geolocation) {
      showToast(t(LangKeys.biz_profile__gps_turn_on_toast) || "Geolocation is not supported by your browser.", "error");
      return;
    }

    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          let detectedState = profile?.state || "";
          let detectedCity = profile?.city || "";
          let detectedArea = profile?.area || "";
          let detectedPincode = profile?.pincode || "";

          try {
            const geoRes = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`
            );
            const geoData = await geoRes.json();
            if (geoData && geoData.address) {
              const a = geoData.address;
              if (a.state) detectedState = a.state;
              if (a.state_district || a.city || a.county) {
                detectedCity = a.city || a.state_district || a.county || "";
              }
              if (a.suburb || a.village || a.neighbourhood || a.road || a.residential) {
                detectedArea = a.village || a.suburb || a.neighbourhood || a.residential || a.road || "";
              }
              if (a.postcode) detectedPincode = a.postcode;
            }
          } catch (e) {
            console.log("Nominatim reverse geocode error:", e);
          }

          const token = localStorage.getItem("token");
          const updateBody = {
            latitude: lat,
            longitude: lon,
          };
          if (detectedCity) updateBody.city = detectedCity;
          if (detectedState) updateBody.state = detectedState;
          if (detectedArea) updateBody.area = detectedArea;
          if (detectedPincode) updateBody.pincode = detectedPincode;

          const res = await fetch("https://khata.moxpal.com/api/v2/index.php/business-profile/location", {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updateBody),
          });

          const json = await res.json();
          if (json.success) {
            const updatedProfile = {
              ...(profile || emptyProfile),
              latitude: lat,
              longitude: lon,
              state: detectedState || profile?.state,
              city: detectedCity || profile?.city,
              area: detectedArea || profile?.area,
              pincode: detectedPincode || profile?.pincode,
            };
            setProfile(updatedProfile);
            localStorage.setItem("business_profile", JSON.stringify(updatedProfile));
            showToast(t(LangKeys.biz_profile__gps_autofill_success_toast) || "Location & Address auto-filled successfully!");
          } else {
            showToast("Failed to save location", "error");
          }
        } catch (e) {
          console.error("GPS error:", e);
          showToast(t(LangKeys.biz_profile__gps_error_toast) || "Error fetching location", "error");
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        setIsFetchingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          showToast(t(LangKeys.biz_profile__gps_permission_denied_toast) || "Location permission was denied.", "error");
        } else {
          showToast(t(LangKeys.biz_profile__gps_turn_on_toast) || "Please enable GPS/Location in your browser.", "error");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  if (activePage === "upload-logo") {
    return <UploadLogoPage setActivePage={setActivePage} />;
  }

  if (activePage === "edit-field") {
    return (
      <EditFieldPage
        editField={editField}
        setActivePage={setActivePage}
        onSuccess={loadProfile}
      />
    );
  }

  if (activePage === "gstin") {
    return <GstinPage setActivePage={setActivePage} onSuccess={loadProfile} />;
  }

  if (activePage === "bank") {
    return <BankDetailsPage setActivePage={setActivePage} onSuccess={loadProfile} />;
  }

  if (activePage === "staff") {
    return <StaffCountPage setActivePage={setActivePage} onSuccess={loadProfile} />;
  }

  if (loading) {
    return <div style={styles.loading}>Loading business profile...</div>;
  }

  const isStoreOnline = Number(profile?.is_store_online) === 1;
  const hasGps =
    profile?.latitude != null &&
    profile?.longitude != null &&
    Number(profile?.latitude) !== 0 &&
    Number(profile?.longitude) !== 0;

  return (
    <div style={styles.page}>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            ...styles.toast,
            backgroundColor: toastMessage.type === "error" ? "#DC2626" : "#15803D",
          }}
        >
          {toastMessage.type === "error" ? "⚠️" : "✅"} {toastMessage.msg}
        </div>
      )}

      {/* Benefits Modal */}
      {showBenefitsModal && (
        <div style={styles.modalOverlay} onClick={() => setShowBenefitsModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                🛍️ {t(LangKeys.biz_profile__benefits_modal_title) || "Online Business Benefits"}
              </h3>
              <button
                style={styles.modalCloseBtn}
                onClick={() => setShowBenefitsModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.benefitStep}>
                <div style={styles.stepBadge}>1</div>
                <div>
                  <h4 style={styles.stepTitle}>
                    {t(LangKeys.biz_profile__benefits_step1_title) || "1. What happens when active?"}
                  </h4>
                  <p style={styles.stepDesc}>
                    {t(LangKeys.biz_profile__benefits_step1_desc) ||
                      "Your business, products and services go live immediately in Moxpal Local Market. Nearby customers can discover you."}
                  </p>
                </div>
              </div>

              <div style={styles.benefitStep}>
                <div style={styles.stepBadge}>2</div>
                <div>
                  <h4 style={styles.stepTitle}>
                    {t(LangKeys.biz_profile__benefits_step2_title) || "2. What you need to do?"}
                  </h4>
                  <p style={styles.stepDesc}>
                    {t(LangKeys.biz_profile__benefits_step2_desc) ||
                      "• Click the ⚡ 1-Click button to auto-fill State, City, Area, Pincode and GPS.\n• Add your products/services so customers can see them."}
                  </p>
                </div>
              </div>

              <div style={styles.benefitStep}>
                <div style={styles.stepBadge}>3</div>
                <div>
                  <h4 style={styles.stepTitle}>
                    {t(LangKeys.biz_profile__benefits_step3_title) || "3. What are the key benefits?"}
                  </h4>
                  <p style={styles.stepDesc}>
                    {t(LangKeys.biz_profile__benefits_step3_desc) ||
                      "• 0% Commission (100% Free) — No middleman.\n• Direct WhatsApp & Call orders.\n• 24/7 Digital Storefront."}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.modalOkBtn}
                onClick={() => setShowBenefitsModal(false)}
              >
                {t(LangKeys.biz_profile__benefits_btn_got_it) || "Got it, Thanks 👍"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <h2 style={styles.pageTitle}>{t(LangKeys.book_profile)}</h2>
        <p style={styles.pageSub}>{t(LangKeys.business_settings_desc)}</p>
      </div>

      {/* Profile Card & Strength */}
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          {profile?.logo_url ? (
            <img src={profile.logo_url} alt="Business Logo" style={styles.logo} />
          ) : (
            <div style={styles.logoPlaceholder}>🏪</div>
          )}
        </div>

        <div style={styles.addPhoto} onClick={() => setActivePage("upload-logo")}>
          {t(LangKeys.add_photo)}
        </div>

        <div style={styles.strengthRow}>
          <span style={styles.strengthLabel}>
            {t(LangKeys.profile_strength)} :{" "}
            <span style={{ color: strength.color, fontWeight: 700 }}>{strength.label}</span>
          </span>
          <span style={styles.percent}>{strength.percent}%</span>
        </div>

        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              width: `${strength.percent}%`,
              backgroundColor: strength.color,
            }}
          />
        </div>
      </div>

      {/* Section 1: Personal Info */}
      <Section
        title={t(LangKeys.personal_info)}
        rows={[
          ["👤", t(LangKeys.name), profile?.owner_name || t(LangKeys.add_name), "owner_name"],
          ["📞", t(LangKeys.registered_number), profile?.mobile || t(LangKeys.add_mobile), "mobile"],
        ]}
        onRowClick={(field, value) => {
          setEditField({ field, value: value?.startsWith("Add") ? "" : value });
          setActivePage("edit-field");
        }}
      />

      {/* Section 2: Business Info */}
      <Section
        title={t(LangKeys.business_info)}
        rows={[
          ["🏢", t(LangKeys.business_name), profile?.business_name || t(LangKeys.add_business), "business_name"],
          ["📍", t(LangKeys.business_address), profile?.address || t(LangKeys.add_address), "address"],
        ]}
        onRowClick={(field, value) => {
          setEditField({ field, value: value?.startsWith("Add") ? "" : value });
          setActivePage("edit-field");
        }}
      />

      {/* Section 3: Online Store / Moxpal Market Settings */}
      <div style={styles.section}>
        <div style={styles.sectionHead}>
          {t(LangKeys.biz_profile__marketplace_settings) || "ऑनलाइन स्टोर सेटिंग्स (Moxpal Market)"}
        </div>

        <div style={{ padding: "16px" }}>
          {/* Switch Toggle Row */}
          <div style={styles.onlineToggleRow}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
              <span style={{ fontSize: "24px" }}>🛍️</span>
              <div>
                <div style={styles.onlineToggleTitle}>
                  {t(LangKeys.biz_profile__online_business_toggle) || "ऑनलाइन बिज़नेस चालू रखें"}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    marginTop: "2px",
                    color: isStoreOnline
                      ? hasGps
                        ? "#15803D"
                        : "#D97706"
                      : "#DC2626",
                  }}
                >
                  {isStoreOnline
                    ? hasGps
                      ? t(LangKeys.biz_profile__online_business_active) || "बिज़नेस ऑनलाइन चालू है (Active)"
                      : t(LangKeys.biz_profile__gps_pending_badge) || "ऑनलाइन चालू है (GPS सेट करें)"
                    : t(LangKeys.biz_profile__online_business_offline) || "बिज़नेस ऑनलाइन बंद है (Offline)"}
                </div>
              </div>
            </div>

            {/* iOS style Toggle Switch */}
            <label style={styles.switchLabel}>
              <input
                type="checkbox"
                checked={isStoreOnline}
                onChange={toggleOnlineStore}
                style={styles.switchInput}
              />
              <span
                style={{
                  ...styles.switchSlider,
                  backgroundColor: isStoreOnline ? "#16A34A" : "#CBD5E1",
                }}
              >
                <span
                  style={{
                    ...styles.switchKnob,
                    transform: isStoreOnline ? "translateX(24px)" : "translateX(2px)",
                  }}
                />
              </span>
            </label>
          </div>

          <p style={styles.onlineDescText}>
            {isStoreOnline
              ? t(LangKeys.biz_profile__online_business_sub_active) ||
                "चालू करने के बाद 1-क्लिक में राज्य, ज़िला, इलाका व पिनकोड भरें और GPS लोकेशन सेट करें, ताकि आपके प्रोडक्ट और सेवाएं आसपास के ग्राहकों को तुरंत ऑनलाइन दिख सकें।"
              : t(LangKeys.biz_profile__online_business_sub_offline) ||
                "बिज़नेस को ऑनलाइन चालू करें ताकि आसपास के ग्राहक आपके प्रोडक्ट और सेवाएं लोकल मार्केट में खोज सकें।"}
          </p>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
            {/* Know Benefits Button */}
            <button
              type="button"
              onClick={() => setShowBenefitsModal(true)}
              style={styles.benefitsBtn}
            >
              💡 {t(LangKeys.biz_profile__know_benefits_btn) || "इसके फायदे व नियम जानें"} ›
            </button>

            {/* Video Guide Link */}
            <a
              href="https://youtu.be/KLtnoJaRSk4"
              target="_blank"
              rel="noreferrer"
              style={styles.videoGuideBtn}
            >
              <span style={styles.playIconBox}>▶</span>
              <div>
                <div style={styles.videoGuideTitle}>
                  {t(LangKeys.biz_market_video_guide_title) || "लोकल बाज़ार में दुकान ऑनलाइन कैसे करें?"}
                </div>
                <div style={styles.videoGuideSub}>
                  {t(LangKeys.biz_market_video_guide_sub) || "1 मिनट का वीडियो देखें • YouTube Guide"}
                </div>
              </div>
            </a>
          </div>

          {/* GPS Warning Banner if online but GPS missing */}
          {isStoreOnline && !hasGps && (
            <div style={styles.warningBox}>
              <span style={{ fontSize: "18px" }}>⚠️</span>
              <span style={{ fontSize: "12px", color: "#92400E", fontWeight: "600", lineHeight: "1.4" }}>
                {t(LangKeys.biz_profile__gps_live_warning) ||
                  "स्टोर ऑनलाइन है पर GPS सेट नहीं है! कृपया ⚡ 1-क्लिक बटन से GPS सेट करें ताकि दुकान ग्राहकों को दिख सके।"}
              </span>
            </div>
          )}
        </div>

        {/* Extended Online Store Address & GPS fields (Visible when Online) */}
        {isStoreOnline && (
          <div style={{ borderTop: "1px solid #E5E7EB" }}>
            {/* 1-Click Auto Fill Button */}
            <div style={{ padding: "14px 16px", backgroundColor: "#F8FAFC" }}>
              <button
                type="button"
                onClick={handleGpsAutoFill}
                disabled={isFetchingLocation}
                style={styles.autoFillBtn}
              >
                {isFetchingLocation ? (
                  <div style={styles.autoFillSpinner} />
                ) : (
                  <span style={{ fontSize: "18px" }}>⚡</span>
                )}
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div style={styles.autoFillTitle}>
                    {isFetchingLocation
                      ? t(LangKeys.biz_profile__autofill_searching) || "लोकेशन खोजी जा रही है..."
                      : t(LangKeys.biz_profile__autofill_btn_title) || "⚡ 1-क्लिक में पूरा पता ऑटो भरें"}
                  </div>
                  <div style={styles.autoFillSub}>
                    {t(LangKeys.biz_profile__autofill_btn_sub) ||
                      "GPS से राज्य, ज़िला, गाँव व पिनकोड अपने-आप भरें"}
                  </div>
                </div>
                <span style={{ fontSize: "18px", opacity: 0.8 }}>›</span>
              </button>
            </div>

            {/* State Row */}
            <div
              style={styles.row}
              onClick={() => {
                setEditField({ field: "state", value: profile?.state || "" });
                setActivePage("edit-field");
              }}
            >
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>🗺️</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__state_lbl) || "राज्य (State)"}</div>
                  <div style={styles.rowValue}>
                    {profile?.state || t(LangKeys.biz_profile__state_add) || "राज्य जोड़ें"}
                  </div>
                </div>
              </div>
              <div style={styles.chevron}>›</div>
            </div>

            {/* City Row */}
            <div
              style={styles.row}
              onClick={() => {
                setEditField({ field: "city", value: profile?.city || "" });
                setActivePage("edit-field");
              }}
            >
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>🏙️</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__city_lbl) || "शहर / ज़िला (City / District)"}</div>
                  <div style={styles.rowValue}>
                    {profile?.city || t(LangKeys.biz_profile__city_add) || "शहर जोड़ें"}
                  </div>
                </div>
              </div>
              <div style={styles.chevron}>›</div>
            </div>

            {/* Area Row */}
            <div
              style={styles.row}
              onClick={() => {
                setEditField({ field: "area", value: profile?.area || "" });
                setActivePage("edit-field");
              }}
            >
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>🏘️</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__area_lbl) || "गाँव / कस्बा / इलाका (Area / Village)"}</div>
                  <div style={styles.rowValue}>
                    {profile?.area || t(LangKeys.biz_profile__area_add) || "गाँव/इलाका जोड़ें"}
                  </div>
                </div>
              </div>
              <div style={styles.chevron}>›</div>
            </div>

            {/* Pincode Row */}
            <div
              style={styles.row}
              onClick={() => {
                setEditField({ field: "pincode", value: profile?.pincode || "" });
                setActivePage("edit-field");
              }}
            >
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>📮</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__pincode_lbl) || "पिनकोड (Pincode)"}</div>
                  <div style={styles.rowValue}>
                    {profile?.pincode || t(LangKeys.biz_profile__pincode_add) || "पिनकोड जोड़ें"}
                  </div>
                </div>
              </div>
              <div style={styles.chevron}>›</div>
            </div>

            {/* Tagline Row */}
            <div
              style={styles.row}
              onClick={() => {
                setEditField({ field: "store_description", value: profile?.store_description || "" });
                setActivePage("edit-field");
              }}
            >
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>📝</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__tagline_lbl) || "बिज़नेस की टैगलाइन/विवरण"}</div>
                  <div style={styles.rowValue}>
                    {profile?.store_description || t(LangKeys.biz_profile__tagline_add) || "टैगलाइन जोड़ें"}
                  </div>
                </div>
              </div>
              <div style={styles.chevron}>›</div>
            </div>

            {/* GPS Location Row */}
            <div style={styles.gpsRow}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                <span style={{ fontSize: "20px" }}>📍</span>
                <div>
                  <div style={styles.rowLabel}>{t(LangKeys.biz_profile__gps_lbl) || "बिज़नेस की GPS लोकेशन"}</div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: hasGps ? "700" : "500",
                      color: hasGps ? "#15803D" : "#64748B",
                      marginTop: "2px",
                    }}
                  >
                    {hasGps
                      ? `Lat: ${Number(profile.latitude).toFixed(4)}, Lng: ${Number(profile.longitude).toFixed(4)}`
                      : t(LangKeys.biz_profile__gps_not_set) || "लोकेशन अभी सेट नहीं है"}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGpsAutoFill}
                disabled={isFetchingLocation}
                style={styles.gpsBtn}
              >
                {isFetchingLocation
                  ? t(LangKeys.biz_profile__gps_searching) || "खोज रहे हैं..."
                  : hasGps
                  ? t(LangKeys.biz_profile__gps_update_btn) || "अपडेट करें"
                  : t(LangKeys.biz_profile__gps_get_btn) || "GPS लोकेशन लें"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section 4: Financial Info */}
      <Section
        title={t(LangKeys.financial_info)}
        rows={[
          ["🧾", t(LangKeys.gstin), profile?.gstin || t(LangKeys.add_details), "gstin"],
          ["🏦", t(LangKeys.bank_account), profile?.bank_name || t(LangKeys.add_details), "bank"],
        ]}
        onRowClick={(field) => {
          if (field === "gstin") setActivePage("gstin");
          if (field === "bank") setActivePage("bank");
        }}
      />

      {/* Section 5: Staff Info */}
      <Section
        title={t(LangKeys.staff_info)}
        rows={[["👥", t(LangKeys.details), `${profile?.staff_count || 0} Staff`, "staff"]]}
        onRowClick={() => setActivePage("staff")}
      />
    </div>
  );
}

// ======================= Reusable Section Component =======================

function Section({ title, rows, onRowClick }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionHead}>{title}</div>
      {rows.map((row, index) => {
        const [icon, label, value, field] = row;
        return (
          <div key={index} style={styles.row} onClick={() => onRowClick(field, value)}>
            <div style={styles.rowLeft}>
              <span style={styles.rowIcon}>{icon}</span>
              <div>
                <div style={styles.rowLabel}>{label}</div>
                <div style={styles.rowValue}>{value}</div>
              </div>
            </div>
            <div style={styles.chevron}>›</div>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  page: { padding: "14px", fontFamily: "'Outfit', sans-serif" },
  loading: { padding: "20px", fontSize: "14px", color: "#64748B" },
  header: { marginBottom: "14px" },
  pageTitle: { fontSize: "24px", fontWeight: "700", color: "#111827", margin: 0 },
  pageSub: { fontSize: "14px", color: "#6B7280", marginTop: "4px", marginBottom: 0 },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "20px",
    border: "1px solid #E5E7EB",
    marginBottom: "14px",
  },
  logoWrap: { display: "flex", justifyContent: "center" },
  logo: { width: "90px", height: "90px", borderRadius: "50%", objectFit: "cover" },
  logoPlaceholder: {
    width: "90px",
    height: "90px",
    borderRadius: "50%",
    background: "#E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "34px",
  },
  addPhoto: {
    textAlign: "center",
    marginTop: "10px",
    color: "#2563EB",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
  },
  strengthRow: {
    marginTop: "18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  strengthLabel: { fontSize: "13px", color: "#111827" },
  percent: { fontSize: "13px", color: "#111827" },
  progressTrack: {
    marginTop: "8px",
    height: "8px",
    borderRadius: "999px",
    background: "#E5E7EB",
    overflow: "hidden",
  },
  progressFill: { height: "100%", borderRadius: "999px", transition: "width 0.3s ease" },

  section: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    marginBottom: "14px",
    overflow: "hidden",
  },
  sectionHead: {
    padding: "12px 16px",
    background: "#F9FAFB",
    fontSize: "13px",
    fontWeight: "700",
    color: "#475569",
    borderBottom: "1px solid #F1F5F9",
  },
  row: {
    padding: "14px 16px",
    borderTop: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "background-color 0.15s",
  },
  rowLeft: { display: "flex", alignItems: "center", gap: "12px" },
  rowIcon: { width: "20px", textAlign: "center", fontSize: "18px" },
  rowLabel: { fontSize: "12px", color: "#6B7280" },
  rowValue: { fontSize: "15px", color: "#111827", marginTop: "2px", fontWeight: "600" },
  chevron: { fontSize: "20px", color: "#9CA3AF" },

  // Online Store Styles
  onlineToggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  onlineToggleTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#0F172A",
  },
  onlineDescText: {
    fontSize: "12px",
    color: "#64748B",
    marginTop: "8px",
    marginBottom: 0,
    lineHeight: "1.5",
  },
  switchLabel: {
    position: "relative",
    display: "inline-block",
    width: "50px",
    height: "28px",
    cursor: "pointer",
    flexShrink: 0,
  },
  switchInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  switchSlider: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "34px",
    transition: "0.3s",
    display: "flex",
    alignItems: "center",
  },
  switchKnob: {
    height: "24px",
    width: "24px",
    backgroundColor: "white",
    borderRadius: "50%",
    transition: "0.3s",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    display: "inline-block",
  },
  benefitsBtn: {
    padding: "8px 12px",
    backgroundColor: "#F0FDF4",
    border: "1px solid #BBF7D0",
    borderRadius: "10px",
    color: "#15803D",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "inherit",
  },
  videoGuideBtn: {
    padding: "8px 12px",
    backgroundColor: "#EFF6FF",
    border: "1px solid #BFDBFE",
    borderRadius: "10px",
    color: "#1E40AF",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
  },
  playIconBox: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#DC2626",
    color: "white",
    fontSize: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  videoGuideTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#1E3A8A",
  },
  videoGuideSub: {
    fontSize: "10px",
    color: "#64748B",
  },
  warningBox: {
    marginTop: "12px",
    padding: "10px 14px",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  autoFillBtn: {
    width: "100%",
    padding: "12px 16px",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 10px rgba(37, 99, 235, 0.2)",
    fontFamily: "inherit",
  },
  autoFillTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "white",
  },
  autoFillSub: {
    fontSize: "11px",
    color: "#DBEAFE",
    marginTop: "1px",
  },
  autoFillSpinner: {
    width: "18px",
    height: "18px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  gpsRow: {
    padding: "14px 16px",
    borderTop: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  gpsBtn: {
    padding: "8px 14px",
    backgroundColor: "#2563EB",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
  },

  // Modal Styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "16px",
  },
  modalCard: {
    backgroundColor: "white",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "520px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "18px 20px",
    borderBottom: "1px solid #F1F5F9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#0F172A",
    margin: 0,
  },
  modalCloseBtn: {
    border: "none",
    background: "#F1F5F9",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "700",
    color: "#64748B",
  },
  modalBody: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "65vh",
    overflowY: "auto",
  },
  benefitStep: {
    display: "flex",
    gap: "14px",
    alignItems: "flex-start",
  },
  stepBadge: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    backgroundColor: "#DBEAFE",
    color: "#1E40AF",
    fontSize: "13px",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "2px",
  },
  stepTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#0F172A",
    margin: "0 0 4px 0",
  },
  stepDesc: {
    fontSize: "13px",
    color: "#475569",
    margin: 0,
    lineHeight: "1.5",
    whiteSpace: "pre-line",
  },
  modalFooter: {
    padding: "14px 20px",
    borderTop: "1px solid #F1F5F9",
    display: "flex",
    justifyContent: "flex-end",
  },
  modalOkBtn: {
    padding: "10px 24px",
    backgroundColor: "#16A34A",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    color: "white",
    padding: "12px 20px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.2)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

export default BusinessProfileHome;